// MarginMonitorPage — Real-time view of all open positions + margin usage
// Section 13.13: No single trade can use more than 30% of available margin
// Live gauge showing current margin %, breaches highlighted in red

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow, TfProgress } from '../../components/TfPrimitives';

interface Position {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  qty: number;
  entry: number;
  current: number;
  contractMargin: number;     // in $
  accountBalance: number;
  marginPct: number;          // % of available
  status: 'safe' | 'warning' | 'breach';
}

const ACCOUNT_BALANCE = 102480;
const MAX_MARGIN_PCT = 30;
const AVAILABLE_MARGIN = 30744;  // 30% of balance

const MOCK_POS: Position[] = [
  { id: 'p1', symbol: 'ES', side: 'long',  qty: 2, entry: 5430.25, current: 5448.50, contractMargin: 11000, accountBalance: ACCOUNT_BALANCE, marginPct: 11, status: 'safe' },
  { id: 'p2', symbol: 'NQ', side: 'short', qty: 1, entry: 19250.00, current: 19205.00, contractMargin: 14500, accountBalance: ACCOUNT_BALANCE, marginPct: 14, status: 'safe' },
  { id: 'p3', symbol: 'CL', side: 'long',  qty: 1, entry: 78.40, current: 78.85,    contractMargin: 6500, accountBalance: ACCOUNT_BALANCE, marginPct: 6, status: 'safe' },
];

export function MarginMonitorPage() {
  const nav = useNavigate();
  const [positions] = useState(MOCK_POS);
  const totalMargin = positions.reduce((sum, p) => sum + p.contractMargin, 0);
  const totalPct = (totalMargin / ACCOUNT_BALANCE) * 100;
  const isSafe = totalPct <= MAX_MARGIN_PCT;
  const totalPnl = positions.reduce((sum, p) => {
    const pnlPerUnit = p.side === 'long' ? (p.current - p.entry) : (p.entry - p.current);
    return sum + pnlPerUnit * p.qty;
  }, 0);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Margin monitor</div>
        <div className="w-9" />
      </div>

      {/* Hero — white floating card with massive margin % + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>{isSafe ? '✓ Within limits' : '⛔ Limit exceeded'}</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: isSafe ? '#1C1C1E' : '#FF3B30' }}>{totalPct.toFixed(1)}%</span>
            <span className="text-[15px] text-tf-text-3">used</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">of balance · max {MAX_MARGIN_PCT}%</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => nav('/portal/trading-rules')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Rules
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => alert('All positions closed at market.')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Close all
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
          <div className="mt-4 px-4">
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-tf-surface-1">
              <div className="absolute top-0 left-0 h-full bg-tf-blue rounded-full" style={{ width: `${Math.min(totalPct, 100)}%` }} />
              <div className="absolute top-0 h-full w-0.5 bg-tf-loss" style={{ left: `${MAX_MARGIN_PCT}%` }} />
            </div>
            <div className="mt-1 flex justify-between text-[9px] text-tf-text-3">
              <span>$0</span>
              <span className="font-bold">${(ACCOUNT_BALANCE * MAX_MARGIN_PCT / 100).toLocaleString()} ({MAX_MARGIN_PCT}%)</span>
              <span>${ACCOUNT_BALANCE.toLocaleString()}</span>
            </div>
          </div>
        </TfCardFloating>
      </div>

      {/* 3-up stat row */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Used', value: `$${totalMargin.toLocaleString()}`, color: '#1C1C1E' },
          { label: 'Available', value: `$${(AVAILABLE_MARGIN - totalMargin).toLocaleString()}`, color: '#34C759' },
          { label: 'Positions', value: `${positions.length}`, color: '#007AFF' },
        ]}
      />

      {/* 30% rule explainer */}
      <div className="mx-4 mt-3">
        <TfCard className="p-4">
          <h2 className="text-[14px] font-bold text-tf-text-1">The 30% margin rule</h2>
          <p className="mt-1.5 text-[13px] text-tf-text-2 leading-relaxed">
            Per Section 13.13, no single trade idea can use more than <span className="font-bold text-tf-loss">30% of available margin</span>. This prevents gambling behavior. The bot will refuse to place orders above this threshold.
          </p>
        </TfCard>
      </div>

      {/* Positions */}
      <TfSectionHeaderView title="Open positions" viewAllLabel="View all" onViewAll={() => {}} />
      <div className="mx-4">
        <TfCard>
          {positions.map((p, i) => {
            const warn = p.marginPct > 25 && p.marginPct <= 30;
            const breach = p.marginPct > 30;
            return (
              <div key={p.id} data-testid={`pos-${p.id}`} className={['px-4 py-3', i < positions.length - 1 ? 'border-b border-tf-border' : '',
                warn ? 'bg-tf-orange-tint' : breach ? 'bg-tf-red-tint' : ''].join(' ')}>
                <div className="flex items-center gap-3">
                  <div className={['flex h-10 w-10 items-center justify-center rounded-[10px] text-[12px] font-black',
                    p.side === 'long' ? 'bg-tf-green-tint text-tf-profit' : 'bg-tf-red-tint text-tf-loss'
                  ].join(' ')}>{p.side === 'long' ? '↑' : '↓'}</div>
                  <div className="flex-1">
                    <div className="text-[14px] font-bold text-tf-text-1">{p.symbol} <span className="text-[11px] font-normal text-tf-text-3">× {p.qty}</span></div>
                    <div className="text-[11px] text-tf-text-3 tabular-nums">Margin ${p.contractMargin.toLocaleString()}</div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-tf-surface-1">
                      <div
                        className={['h-full rounded-full', breach ? 'bg-tf-loss' : warn ? 'bg-tf-orange' : 'bg-tf-profit'].join(' ')}
                        style={{ width: `${Math.min(p.marginPct * 2.5, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={['text-[15px] font-bold tabular-nums', breach ? 'text-tf-loss' : warn ? 'text-tf-warning' : 'text-tf-text-1'].join(' ')}>{p.marginPct}%</div>
                    <div className="text-[10px] text-tf-text-3">of balance</div>
                  </div>
                </div>
              </div>
            );
          })}
        </TfCard>
      </div>

      {/* Contract limits by tier */}
      <TfSectionHeaderView title="Contract limits by tier" />
      <div className="mx-4">
        <TfCard>
          {[
            { tier: 'Beginner',     contracts: '5 Standard / 50 Micros',  margin: '$15,000' },
            { tier: 'Mid',          contracts: '10 Standard / 100 Micros', margin: '$30,000' },
            { tier: 'Professional', contracts: '15 Standard / 150 Micros', margin: '$45,000' },
          ].map((t, i, a) => (
            <div key={t.tier} data-testid={`contract-${t.tier}`} className={['flex items-center justify-between px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <div>
                <div className="text-[14px] font-semibold text-tf-text-1">{t.tier}</div>
                <div className="text-[11px] text-tf-text-3">{t.contracts}</div>
              </div>
              <div className="text-[13px] font-bold tabular-nums text-tf-blue">Max {t.margin}</div>
            </div>
          ))}
        </TfCard>
      </div>
    </div>
  );
}
